export interface CreditAuctionEmployerRegistration {
  Codigo: number;
  Descricao: string;
}

export interface CreditAuctionPoliticallyExposedPerson {
  Codigo: number;
  Descricao: string;
}

export interface CreditAuctionProposal {
  id: string;
  Status: AuctionProposalStatus;
  CustomStatus?: string | null;
  IdSolicitacao: string | number;
  ShortHash: string;
  CreatedAt: string;
  UrlAutocontratacao: string | null;
  Cpf: string;
  Telefone: string;
  Email: string;
  Idade: number | null;
  Sexo: string;
  Matricula: string;
  InscricaoEmpregador: CreditAuctionEmployerRegistration;
  NumeroInscricaoEmpregador: string;
  ValorLiberado: number;
  NroParcelas: number;
  TaxaMensal: number;
  Produto: string;
  DataDesembolso: string | null;
  DataHoraValidadeSolicitacao: string;
  NomeTrabalhador: string;
  DataNascimento: string;
  MargemDisponivel: number;
  MargemReal: number;
  TotalVencimentos: number;
  ElegivelEmprestimo: boolean;
  PessoaExpostaPoliticamente: CreditAuctionPoliticallyExposedPerson;
  DataAdmissao: string;
}

export enum NegotiationSubStep {
  CPF_INCLUSION = "CPF_INCLUSION",
  WHATSAPP_CONFIRMATION = "WHATSAPP_CONFIRMATION",
  SIMULATION = "SIMULATION",
  PERSONAL_DATA = "PERSONAL_DATA",
  ADDRESS = "ADDRESS",
  BANK_DATA = "BANK_DATA",
  ANALISE = "ANALISE",
  PROPOSTA = "PROPOSTA",
  REVISAO = "REVISAO",
  CONSENTIMENTO = "CONSENTIMENTO",
  LOCALIZATION = "LOCALIZATION",
  ACEITE_CONTRATACAO = "ACEITE_CONTRATACAO",
  BIOMETRIA = "BIOMETRIA",
  FORMALIZATION = "FORMALIZATION",
}

export enum AuctionProposalStatus {
  CREATED = "CREATED",
  CHOSEN = "CHOSEN",
  PENDING_CONFIRMATION = "PENDING_CONFIRMATION",
  PENDING_FORMALIZATION = "PENDING_FORMALIZATION",
  PENDING_REFORMALIZATION = "PENDING_REFORMALIZATION",
  PENDING_USER_REVIEW = "PENDING_USER_REVIEW",
  PAYED = "PAYED",
  IN_CANCELLATION = "IN_CANCELLATION",
  CANCELED = "CANCELED",
  ERROR_CREATE_BIOMETRIC_VALIDATION = "ERROR_CREATE_BIOMETRIC_VALIDATION",
  REPROVED = "REPROVED",
  REPROVED_IN_PROCESS = "REPROVED_IN_PROCESS",
  PENDING_BANK_ANALYSIS = "PENDING_BANK_ANALYSIS",
  CANCELED_BY_CLIENT = "CANCELED_BY_CLIENT",
  SELFSERVICE = "SELFSERVICE",
  IN_AUCTION = "IN_AUCTION",
  PROCESSING_SIMULATION = "PROCESSING_SIMULATION",
  AWAITING_BORROWER_CHOICE = "AWAITING_BORROWER_CHOICE",
  AGUARDANDO_VALIDADE_LEILAO = "AGUARDANDO_VALIDADE_LEILAO",
}

// Status mostrados por padrão na tabela (e enviados como filtro `status` quando
// nenhum é selecionado — inclusive na busca por CPF). Precisa conter os status
// pós-vitória em andamento, senão uma proposta neles some da busca. Foram
// adicionados PROCESSING_SIMULATION, AWAITING_BORROWER_CHOICE e
// PENDING_USER_REVIEW (antes faltavam — ex.: tomador em "Aguard. Revisão" não
// aparecia na busca por CPF a menos que o status fosse selecionado à mão).
export const VISIBLE_AUCTION_PROPOSAL_STATUSES: AuctionProposalStatus[] = [
  AuctionProposalStatus.CREATED,
  AuctionProposalStatus.CHOSEN,
  AuctionProposalStatus.PENDING_CONFIRMATION,
  AuctionProposalStatus.PROCESSING_SIMULATION,
  AuctionProposalStatus.AWAITING_BORROWER_CHOICE,
  AuctionProposalStatus.PENDING_FORMALIZATION,
  AuctionProposalStatus.PENDING_USER_REVIEW,
  AuctionProposalStatus.PAYED,
  AuctionProposalStatus.REPROVED,
  AuctionProposalStatus.REPROVED_IN_PROCESS,
  AuctionProposalStatus.PENDING_BANK_ANALYSIS,
  AuctionProposalStatus.IN_AUCTION,
];

export interface ResponseAuctionProposalDto {
  id: string;
  shortHash: string;
  dataCompetencia: string | null;
  status: AuctionProposalStatus;
  naturalPersonId: string | null;
  chosenFinancialInfoId: string | null;
  processorId: string | null;
  bankDetailId: string | null;
  unicoDataId: string | null;
  codigoRequisicao: string | null;
  statusCustomizado: string | null;
  documentoTomador: string;
  numeroSolicitacao: number;
  margem: number | null;
  dataHoraValidadeSolicitacao: string;
  valorTaxaMensal: number;
  valorLiberado: number;
  numeroParcelas: number;
  numeroCcb: string | null;
  employerId: string | null;
  marginId: string | null;
  bioGeolocalizacao: Record<string, unknown> | null;
  dataDesembolso: string | null;
  urlComprovantePagamento: string | null;
  createdAt: string;
  updatedAt: string;
  negotiationSubStep?: NegotiationSubStep | null;
}

export interface CreditAuctionProposalFilters {
  statuses: AuctionProposalStatus[];
  wonAuction: boolean | null;
  /** Intervalo pela data de criação da proposta (created_at, YYYY-MM-DD). */
  startDate: string;
  endDate: string;
  /** Intervalo pela data de validade da solicitação (YYYY-MM-DD). */
  validadeStartDate: string;
  validadeEndDate: string;
  minReleasedValue: string;
  maxReleasedValue: string;
  negotiationSubStep: NegotiationSubStep | null;
}

export interface FindOneCompleteAuctionProposalDto {
  proposal: ResponseAuctionProposalDto;
  naturalPerson: Record<string, unknown> | null;
  chosenFinancialInfo: Record<string, unknown> | null;
  processor: Record<string, unknown> | null;
  bankDetail: Record<string, unknown> | null;
  unicoData: Record<string, unknown> | null;
  employer: Record<string, unknown> | null;
  product: Record<string, unknown> | null;
  urlAutocontratacao: string | null;
}

export interface CreateAuctionProposalDto {
  dataCompetencia?: string;
  status: string;
  naturalPersonId?: string;
  chosenFinancialInfoId?: string;
  processorId?: string;
  bankDetailId?: string;
  unicoDataId?: string;
  codigoRequisicao?: string;
  statusCustomizado?: string;
  documentoTomador: string;
  numeroSolicitacao: number;
  margem?: number;
  dataHoraValidadeSolicitacao: string;
  valorTaxaMensal: number;
  valorLiberado: number;
  numeroParcelas: number;
  numeroCcb?: string;
  employerId?: string;
  marginId?: string;
  bioGeolocalizacao?: Record<string, unknown>;
  dataDesembolso?: string;
  urlComprovantePagamento?: string;
}

export type UpdateAuctionProposalDto = Partial<CreateAuctionProposalDto>;

export type AuctionProposalSortField = "data_validade";
export type AuctionProposalSortDirection = "asc" | "desc";

export interface CreditAuctionProposalListParams {
  page: number;
  pageSize: number;
  searchTerm?: string;
  statuses?: AuctionProposalStatus[];
  wonAuction?: boolean;
  startDate?: string;
  endDate?: string;
  validadeStartDate?: string;
  validadeEndDate?: string;
  minReleasedValue?: number;
  maxReleasedValue?: number;
  cpf?: string;
  negotiationSubStep?: NegotiationSubStep;
  sortBy?: AuctionProposalSortField;
  sortDirection?: AuctionProposalSortDirection;
}

export interface CreditAuctionProposalListResponse {
  data: ResponseAuctionProposalDto[];
  total: number;
  pagina_atual: number;
  tamanho_pagina: number;
  total_paginas: number;
  pagina_anterior: number | null;
  proxima_pagina: number | null;
}